import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import JobsList from "@/pages/JobsList";
import NewJob from "@/pages/NewJob";
import JobDetail from "@/pages/JobDetail";
import PrintInvoice from "@/pages/PrintInvoice";
import TrackJob from "@/pages/TrackJob";
import Settings from "@/pages/Settings";
import BulkPrint from "@/pages/BulkPrint";
import CustomerRequest from "@/pages/CustomerRequest";
import QRPoster from "@/pages/QRPoster";
import Analytics from "@/pages/Analytics";
import Scanner from "@/pages/Scanner";
import Inventory from "@/pages/Inventory";
import BarcodeLabels from "@/pages/BarcodeLabels";
import Billing from "@/pages/Billing";
import BillLabels from "@/pages/BillLabels";
import ServiceEntry from "@/pages/ServiceEntry";
import AdminPanel from "@/pages/AdminPanel";
import DataMigration from "@/pages/DataMigration";
import SCEntry from "@/pages/SCEntry";
import SCJobsList from "@/pages/SCJobsList";
import SCJobDetail from "@/pages/SCJobDetail";
import SCTrackPage from "@/pages/SCTrackPage";
import SCPrintJobCard from "@/pages/SCPrintJobCard";
import SCBulkPrint from "@/pages/SCBulkPrint";
import PCBInventory from "@/pages/PCBInventory";
import PCBDispatchDetail from "@/pages/PCBDispatchDetail";
import PCBTrackPage from "@/pages/PCBTrackPage";
import "@/App.css";

// Redirect unauthenticated users to /login
const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public pages */}
      <Route path="/login"       element={<Login />} />
      <Route path="/track"       element={<TrackJob />} />
      <Route path="/sc-track"    element={<SCTrackPage />} />
      <Route path="/pcb-track"   element={<PCBTrackPage />} />
      <Route path="/print"       element={<PrintInvoice />} />
      <Route path="/print/:jobId" element={<PrintInvoice />} />
      <Route path="/bulk-print"  element={<PrivateRoute><BulkPrint /></PrivateRoute>} />
      <Route path="/request"      element={<CustomerRequest />} />
      <Route path="/qr-poster"    element={<QRPoster />} />
      <Route path="/barcode-labels" element={<BarcodeLabels />} />
      <Route path="/bill-labels/:billId" element={<BillLabels />} />

      {/* Staff portal (protected) */}
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index          element={<Dashboard />} />
        <Route path="jobs"    element={<JobsList />} />
        <Route path="jobs/new"    element={<ServiceEntry />} />
        <Route path="jobs/:jobId" element={<JobDetail />} />
            <Route path="settings"   element={<Settings />} />
            <Route path="analytics"  element={<Analytics />} />
            <Route path="admin"      element={<AdminPanel />} />
            <Route path="migrate"    element={<DataMigration />} />
            <Route path="scan"       element={<Scanner />} />
            <Route path="inventory"  element={<Inventory />} />
            {/* PCB Replacement Management — lives under Inventory, isolated from All Jobs / SC Jobs */}
            <Route path="inventory/pcb"                      element={<PCBInventory />} />
            <Route path="inventory/pcb/dispatch/:id"         element={<PCBDispatchDetail />} />
            <Route path="billing"    element={<Billing />} />
            {/* Service Centre list stays inside Layout (needs sidebar nav) */}
            <Route path="sc"         element={<SCJobsList />} />
      </Route>

      {/* Service Centre full-screen pages — no sidebar */}
      <Route path="/sc/new"           element={<PrivateRoute><SCEntry /></PrivateRoute>} />
      <Route path="/sc/:scId"         element={<PrivateRoute><SCJobDetail /></PrivateRoute>} />
      <Route path="/sc-print/:scId"   element={<PrivateRoute><SCPrintJobCard /></PrivateRoute>} />
      <Route path="/sc-bulk-print"    element={<PrivateRoute><SCBulkPrint /></PrivateRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster richColors position="top-right" />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
